import React, {useCallback, useMemo, useState} from 'react';

import {usePageProjectAndEntityTarget} from '../hooks';
import {type JsonObject} from '../utilTypes';

/**
 * overrides import state constants for tracking config import lifecycle.
 *
 * using this in conjunction with userHadEditedOverrides allows us to track whether
 * the overrides were edited by the user or programatically through the config importer.
 *
 * LOADING: initial state, waiting for first configSourceRun to load
 * IMPORTING: actively importing a configSourceRun trigged by user
 * READY: ready for user edits
 */
export const OverridesImportState = {
  LOADING: 'loading',
  IMPORTING: 'importing',
  READY: 'ready',
} as const;

export type OverridesImportStateType =
  (typeof OverridesImportState)[keyof typeof OverridesImportState];

type JobDrawerStateContextType = {
  overrides: string;
  overridesError: string;
  structuredOverrides: JsonObject;
  structuredFileOverrides: JsonObject | undefined;
  targetEntityName: string;
  targetProjectName: string;
  overridesImportState: OverridesImportStateType;
  userHasEditedOverrides: boolean;
  onOverridesChange: (value: string) => void;
  onStructuredOverridesChange: (config: JsonObject, files?: JsonObject) => void;
  setOverridesError: (error: string) => void;
  setTargetEntityName: (entityName: string) => void;
  setTargetProjectName: (projectName: string) => void;
  beginImport: () => void;
  completeImport: () => void;
  initializeForJob: (jobId: string) => void;
};

const JobDrawerStateContext = React.createContext<
  JobDrawerStateContextType | undefined
>(undefined);

// this context persists user edits (overrides and destination project) when
// navigating between views or closing/reopening the drawer. this prevents users
// from losing their work when they go to create secrets or when reopening and closing the drawer.
export const JobDrawerStateProvider: React.FC<{
  children: React.ReactNode;
  rerunFromRunName?: string;
}> = ({children, rerunFromRunName}) => {
  const target = usePageProjectAndEntityTarget();

  // use the ref to prevent infinite re-renders
  const initialTargetEntityName = React.useRef(target.targetEntityName);
  const initialTargetProjectName = React.useRef(target.targetProjectName);

  const [overrides, setOverrides] = useState('');
  const [overridesError, setOverridesError] = useState('');
  const [structuredOverrides, setStructuredOverrides] = useState<JsonObject>(
    {}
  );
  const [structuredFileOverrides, setStructuredFileOverrides] = useState<
    JsonObject | undefined
  >(undefined);

  const [targetEntityName, setTargetEntityName] = useState<string>(
    // oxlint-disable-next-line react/refs
    initialTargetEntityName.current
  );
  const [targetProjectName, setTargetProjectName] = useState<string>(
    // oxlint-disable-next-line react/refs
    initialTargetProjectName.current
  );

  const [overridesImportState, setOverridesImportState] =
    useState<OverridesImportStateType>(OverridesImportState.LOADING);
  const [userHasEditedOverrides, setUserHasEditedOverrides] = useState(false);

  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [currentRunName, setCurrentRunName] = useState<string | null>(
    rerunFromRunName ?? null
  );

  const beginImport = () => {
    setOverridesImportState(OverridesImportState.IMPORTING);
    setUserHasEditedOverrides(false);
  };

  const completeImport = () => {
    setOverridesImportState(OverridesImportState.READY);
  };

  const initializeForJob = useCallback(
    (jobId: string) => {
      if (currentJobId !== jobId || currentRunName !== rerunFromRunName) {
        // only reset state if we're switching from one job/run to another
        // (not when initializing from null, which preserves reopened state)
        const isSwitchingJobOrRun =
          (currentJobId !== null && currentJobId !== jobId) ||
          (currentRunName !== null && currentRunName !== rerunFromRunName);

        if (isSwitchingJobOrRun) {
          // clear state when job or run changes
          setOverrides('');
          setOverridesError('');
          setStructuredOverrides({});
          setStructuredFileOverrides(undefined);
          setOverridesImportState(OverridesImportState.LOADING);
          setUserHasEditedOverrides(false);
          // reset target project to page defaults only when switching jobs
          setTargetEntityName(initialTargetEntityName.current);
          setTargetProjectName(initialTargetProjectName.current);
        }

        setCurrentJobId(jobId);
        setCurrentRunName(rerunFromRunName ?? null);
      }
    },
    [currentJobId, currentRunName, rerunFromRunName]
  );

  const onOverridesChange = useCallback(
    (value: string) => {
      setOverrides(value);
      setOverridesError('');
      // onOverridesChange occurs when there's a change in the overrides
      // which can come programatically (when we import a run) or from the user editing the text editor.
      // we only want to mark as user edit if the change came from the user.
      if (overridesImportState === OverridesImportState.READY) {
        setUserHasEditedOverrides(true);
      }
    },
    [overridesImportState]
  );

  const onStructuredOverridesChange = useCallback(
    (config: JsonObject, files?: JsonObject) => {
      const overridesStr = JSON.stringify(
        {
          args: [],
          entry_point: [],
          run_config: config,
          files,
        },
        null,
        4
      );
      setOverrides(overridesStr);
      setStructuredOverrides(config);
      setStructuredFileOverrides(files);
      setOverridesError('');
      // onStructuredOverridesChange occurs when there's a change in the overrides
      // which can come programatically (when we import a run) or from the user editing the form.
      // we only want to mark as user edit if the change came from the user.
      if (overridesImportState === OverridesImportState.READY) {
        setUserHasEditedOverrides(true);
      }
    },
    [overridesImportState]
  );

  const contextValue: JobDrawerStateContextType = useMemo(
    () => ({
      overrides,
      overridesError,
      structuredOverrides,
      structuredFileOverrides,
      targetEntityName,
      targetProjectName,
      overridesImportState,
      userHasEditedOverrides,
      onOverridesChange,
      onStructuredOverridesChange,
      setOverridesError,
      setTargetEntityName,
      setTargetProjectName,
      beginImport,
      completeImport,
      initializeForJob,
    }),
    [
      overrides,
      overridesError,
      structuredOverrides,
      structuredFileOverrides,
      targetEntityName,
      targetProjectName,
      overridesImportState,
      userHasEditedOverrides,
      onOverridesChange,
      onStructuredOverridesChange,
      initializeForJob,
    ]
  );

  return (
    <JobDrawerStateContext.Provider value={contextValue}>
      {children}
    </JobDrawerStateContext.Provider>
  );
};

export const useJobDrawerState = () => {
  const context = React.useContext(JobDrawerStateContext);
  if (context === undefined) {
    throw new Error(
      'useJobDrawerState must be used within a JobDrawerStateProvider'
    );
  }
  return context;
};
